import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  Component,
  ContentChildren,
  input,
  Input,
  QueryList,
  signal,
} from '@angular/core';
import { TabOptionComponent } from './components/tab-option.component';
import { TabPanelComponent } from './components/tab-panel.component';
import { TabOptionsComponent } from './components/tab-options.component';
import { TabPanelsComponent } from './components/tab-panels.component';

@Component({
  selector: 'app-tabs',
  imports: [CommonModule],
  templateUrl: './tabs.component.html',
  styleUrl: './tabs.component.scss',
})
export class TabsComponent implements AfterContentInit {
  @Input() defaultActive?: string | number;
  showOptions = input(true);

  // collect ALL wrappers projected into this component (including from nested tabs)
  @ContentChildren(TabOptionsComponent, { descendants: true })
  private allOptionsHosts!: QueryList<TabOptionsComponent>;

  @ContentChildren(TabPanelsComponent, { descendants: true })
  private allPanelsHosts!: QueryList<TabPanelsComponent>;

  // the ones that belong to THIS tabs instance
  private optionsHost?: TabOptionsComponent;
  private panelsHost?: TabPanelsComponent;

  private optionsList?: QueryList<TabOptionComponent>;
  private panelsList?: QueryList<TabPanelComponent>;

  activeIndex = signal(0);
  tabCount = signal(0);
  indicatorWidth = signal(0);
  indicatorLeft = signal(0);

  ngAfterContentInit() {
    // pick the wrappers whose ownerTabs === this
    this.optionsHost = this.allOptionsHosts.find((h) => h.ownerTabs === this);
    this.panelsHost = this.allPanelsHosts.find((h) => h.ownerTabs === this);

    // subscribe to JUST those
    this.optionsHost?.optionsChange.subscribe((q) => {
      this.optionsList = q;
      this.relink();
    });
    this.panelsHost?.panelsChange.subscribe((q) => {
      this.panelsList = q;
      this.relink();
    });

    // initial hookup if already present
    if (this.optionsHost?.options) this.optionsList = this.optionsHost.options;
    if (this.panelsHost?.panels) this.panelsList = this.panelsHost.panels;
    this.relink();
  }

  private updateIndicator() {
    const c = this.tabCount();
    const i = this.activeIndex();
    const w = c ? 100 / c : 0;
    this.indicatorWidth.set(w);
    this.indicatorLeft.set(i * w);
  }

  private relink() {
    const opts = this.optionsList?.toArray() ?? [];
    const pans = this.panelsList?.toArray() ?? [];

    this.tabCount.set(opts.length);

    opts.forEach((o, i) => {
      o.index = i;
      o.parent = this;
    });
    pans.forEach((p, i) => {
      p.index = i;
      p.parent = this;
      p.updateVisibility(this.activeIndex());
    });

    // defaultActive (value or index, 1-based or 0-based)
    let startIdx = 0;
    if (this.defaultActive !== undefined) {
      const norm = (v: unknown) => String(v ?? '').trim();
      let found = opts.findIndex(
        (o) => norm(o.value) === norm(this.defaultActive)
      );
      if (found < 0) {
        const n = Number(norm(this.defaultActive));
        if (Number.isFinite(n)) {
          if (n >= 1 && n <= opts.length) found = n - 1;
          else if (n >= 0 && n < opts.length) found = n;
        }
      }
      if (found >= 0) startIdx = found;
    }

    this.selectIndex(startIdx);
    this.updateIndicator();
  }

  selectIndex(i: number) {
    this.activeIndex.set(i);
    this.panelsList?.forEach((p) => p.updateVisibility(i));
    this.updateIndicator();
  }
}
